import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { BehaviorSubject, Subject } from 'rxjs';

import {
  detectDesktopSafariUserAgent,
  isSessionMarkedSafariMainMenu,
  markSessionSafariMainMenu
} from '../../shared/safari-main-menu-session';

export type MenuToggleEvent = 'toggle' | 'open' | 'close';

@Injectable({ providedIn: 'root' })
export class SideMenusService {
  // Existing event buses used across layouts/components
  toggleMainMenuSubject = new Subject<MenuToggleEvent>();
  toggleAltMenuSubject = new Subject<MenuToggleEvent>();
  changeMainMenuModeSubject = new Subject<string>();

  // Used by dynamic menu / side navbar to decide what to render in alt sidenav
  renderAltMenuSubject = new Subject<'settings' | 'notifications' | 'search' | string>();

  // Advisory open events (keeps advisory button behavior consistent)
  openAdvisorySubject = new Subject<void>();

  /**
   * Safari-only: beta / mobile shells should use `app-main-menu` (replica) instead of `app-beta-menu`.
   * Lives on this root service so it survives `DynamicLayout` portal swaps and route changes.
   * Once true, never returns to false for the tab session.
   */
  private readonly safariBetaShellUsesMainMenuSubject = new BehaviorSubject<boolean>(false);
  readonly safariBetaShellUsesMainMenu$ = this.safariBetaShellUsesMainMenuSubject.asObservable();

  // Shared open-state (single source of truth)
  private readonly mainMenuOpenSubject = new BehaviorSubject<boolean>(false);

  // ✅ New API you wanted
  readonly mainMenuOpen$ = this.mainMenuOpenSubject.asObservable();

  // ✅ Legacy compatibility (your top navbar previously used `.value`)
  readonly mainMenuStateSubject = this.mainMenuOpenSubject;

  getMainMenuState(): boolean {
    return this.mainMenuOpenSubject.value;
  }

  // ✅ Legacy helper methods used in many components (your compile errors)
  openMainMenu(): void {
    this.toggleMainMenuSubject.next('open');
  }

  openAdvisory(): void {
    this.openMainMenu();
    this.openAdvisorySubject.next();
  }

  closeMainMenu(): void {
    this.toggleMainMenuSubject.next('close');
  }

  toggleMainMenu(): void {
    this.toggleMainMenuSubject.next('toggle');
  }

  openAltMenu(): void {
    this.toggleAltMenuSubject.next('open');
  }

  closeAltMenu(): void {
    this.toggleAltMenuSubject.next('close');
  }

  toggleAltMenu(): void {
    this.toggleAltMenuSubject.next('toggle');
  }

  // Called by layouts on (openedChange)
  setMainMenuState(opened: boolean): void {
    this.mainMenuOpenSubject.next(!!opened);
  }

  constructor(@Inject(PLATFORM_ID) private readonly platformId: object) {
    if (isPlatformBrowser(this.platformId) && isSessionMarkedSafariMainMenu()) {
      this.safariBetaShellUsesMainMenuSubject.next(true);
    }
  }

  get safariBetaShellUsesMainMenu(): boolean {
    return this.safariBetaShellUsesMainMenuSubject.value;
  }

  /**
   * Re-evaluate Safari (UA, session, body class). Latches to true only — call on navigation / pageshow.
   * Optional `_currentUrl` (e.g. `router.url`) is reserved for shell layouts that must recompute in lockstep
   * with `NavigationEnd` (beta / mobile / sand-storm around `/notes` and `/multi-ai/*`).
   */
  recomputeSafariBetaShellMainMenu(_currentUrl?: string | null): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    if (this.safariBetaShellUsesMainMenuSubject.value) {
      return;
    }
    const session = isSessionMarkedSafariMainMenu();
    const body =
      typeof document !== 'undefined' && !!document.body?.classList?.contains('safari-browser');
    const ua = detectDesktopSafariUserAgent();
    if (session || body || ua) {
      markSessionSafariMainMenu();
      this.safariBetaShellUsesMainMenuSubject.next(true);
    }
  }
}
